import { useEffect, useMemo, useState } from 'react'
import { useSearchParams, useParams } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import CLUB from '../../config/club.js'
import { subscribeMasterRound, setMatchScore, finalizeMatch, claimMatch, myRole } from '../../lib/masterRounds.js'
import { settleRound } from './ggRoundSettle.js'

// Live GROUP round — group play is the frame; the selected game type(s) decide how
// it settles. Scorekeepers enter per-player GROSS; Results run each chosen game
// (net/gross per the Use Handicap switch) + a combined settle-up. QR chain + roles
// unchanged: scan the SCOREKEEPER QR (/admin/gg/mg?r=ID) to claim a group; the
// PLAYER QR (/r/ID) lets players follow read-only.
const PRIMARY = CLUB.primaryColor || 'var(--ge-primary)'
const ACCENT = CLUB.accentColor || 'var(--ge-primary)'
const num = (v) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : null }
const money = (v) => (v > 0 ? `+$${v}` : v < 0 ? `−$${Math.abs(v)}` : '$0')

export default function GMasterRound() {
  const [sp] = useSearchParams()
  const params = useParams()
  const id = String(params.code || sp.get('r') || sp.get('code') || '')
  const [round, setRound] = useState(undefined)
  const [tab, setTab] = useState(null)
  const [showQR, setShowQR] = useState(false)
  const [entry, setEntry] = useState({})
  useEffect(() => subscribeMasterRound(id, setRound), [id])
  const role = useMemo(() => myRole(round), [round])
  const results = useMemo(() => (round ? settleRound(round) : null), [round])

  if (round === undefined) return <div className="card">Loading…</div>
  if (!round) return <div className="card"><p className="text-gray-600">Round not found or expired.</p></div>

  // The app PUBLISHES its computed game state (Wolf/Sixes/… — title, money
  // standings, side bets, per-hole game detail) to round.follow. When present we
  // render THAT verbatim — the real game, read-only — instead of recomputing or
  // falling back to the stroke grid below.
  if (round.follow && Array.isArray(round.follow.standings)) return <FollowView f={round.follow} round={round} />

  const groups = Object.entries(round.matches || {}).sort((a, b) => (a[1].matchNumber || 0) - (b[1].matchNumber || 0))
  const players = round.players || []
  const pById = Object.fromEntries(players.map((p) => [p.id, p]))
  const pars = round.course?.pars || []
  const followUrl = `${location.origin}/r/${id}`
  const canEdit = (mid) => role.organizer || role.scorekeeperOf === mid
  const write = (mid, hole, pid, txt) => { setEntry((e) => ({ ...e, [`${mid}:${hole}:${pid}`]: txt })); setMatchScore(id, mid, hole, pid, txt === '' ? null : Number(txt)).catch((err) => alert('Could not save: ' + (err?.message || err))) }
  const valOf = (mid, hole, pid) => { const k = `${mid}:${hole}:${pid}`; if (entry[k] !== undefined) return entry[k]; const v = round.matches?.[mid]?.scores?.[hole]?.[pid]; return v != null ? String(v) : '' }

  const openMatches = groups.filter(([, m]) => !m.scorekeeperId)
  const needClaim = role.uid && !role.scorekeeperOf
  const activeTab = tab || role.scorekeeperOf || groups[0]?.[0] || 'results'
  const gamesList = (round.games || []).map((g) => g.toUpperCase()).join(' · ')

  return (
    <div className="card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2"><span style={{ fontSize: 22 }}>⛳</span><h3 className="font-bold text-xl">{round.name || 'Group Round'}</h3></div>
          <p className="text-sm text-gray-600">{round.course?.name} · Tee {round.course?.teeName} ({round.course?.rating}/{round.course?.slope}) · {gamesList} · {round.useHandicap ? 'Net' : 'Gross'}</p>
          {role.scorekeeperOf ? <p className="text-sm font-bold mt-1" style={{ color: PRIMARY }}>You're scoring Group {round.matches[role.scorekeeperOf]?.matchNumber}.</p> : role.uid ? <p className="text-sm text-gray-500 mt-1">Tap a group below to claim + score it.</p> : <p className="text-sm text-gray-500 mt-1">Following live (read-only).</p>}
        </div>
        <div className="text-center">
          <button className="text-sm font-bold px-3 py-2 rounded-lg" style={{ background: PRIMARY, color: '#fff' }} onClick={() => setShowQR((v) => !v)}>{showQR ? 'Hide' : 'Show'} Player QR</button>
          {showQR ? (
            <div className="inline-block p-3 mt-2 rounded-lg" style={{ background: '#fff', border: `2px solid ${ACCENT}` }}>
              <QRCodeSVG value={followUrl} size={150} />
              <div className="text-[11px] font-bold mt-1" style={{ color: PRIMARY }}>Players scan to follow</div>
              <div className="flex gap-1 mt-1 justify-center">
                <button className="text-xs font-bold px-2 py-1 rounded" style={{ background: '#EDF4EE', color: PRIMARY }} onClick={() => navigator.clipboard?.writeText(followUrl).then(() => alert('Follow link copied'))}>Copy</button>
                <a className="text-xs font-bold px-2 py-1 rounded" style={{ background: '#EDF4EE', color: PRIMARY }} href={`sms:?&body=${encodeURIComponent('Follow our round live: ' + followUrl)}`}>Text</a>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {needClaim ? (
        <div className="mt-4 p-3 rounded-lg" style={{ background: '#FBF3DC', border: `1px solid ${ACCENT}` }}>
          <div className="text-sm font-extrabold mb-2" style={{ color: '#8A6D1F' }}>Claim your group to score it:</div>
          <div className="flex gap-2 flex-wrap">
            {openMatches.length ? openMatches.map(([mid, m]) => (
              <button key={mid} className="px-3 py-2 rounded-lg text-sm font-bold" style={{ background: PRIMARY, color: '#fff' }} onClick={() => claimMatch(id, mid).catch((e) => alert(e?.message || e))}>Group {m.matchNumber} — {(m.playerIds || []).map((p) => pById[p]?.name).join(', ')}</button>
            )) : <span className="text-sm text-gray-500">All groups claimed.</span>}
          </div>
        </div>
      ) : null}

      <div className="flex gap-2 flex-wrap mt-4 mb-3">
        {groups.map(([mid, m]) => (
          <button key={mid} onClick={() => setTab(mid)} className="px-3 py-1.5 rounded-full text-sm font-bold" style={activeTab === mid ? { background: PRIMARY, color: '#fff' } : { background: '#ECECEA', color: PRIMARY }}>Group {m.matchNumber}{role.scorekeeperOf === mid ? ' ✎' : ''}{m.complete ? ' ✓' : ''}</button>
        ))}
        <button onClick={() => setTab('results')} className="px-3 py-1.5 rounded-full text-sm font-bold" style={activeTab === 'results' ? { background: ACCENT, color: '#1a2e12' } : { background: '#FBF3DC', color: '#8A6D1F' }}>🏆 Results</button>
      </div>

      {activeTab === 'results' ? <ResultsView results={results} /> : (
        <GroupCard mid={activeTab} round={round} pById={pById} pars={pars} editable={canEdit(activeTab)} valOf={valOf} write={write} id={id} />
      )}
    </div>
  )
}

function GroupCard({ mid, round, pById, pars, editable, valOf, write, id }) {
  const m = round.matches?.[mid]; if (!m) return null
  const gp = (m.playerIds || []).map((pid) => pById[pid]).filter(Boolean)
  const teamGame = (round.games || []).some((g) => ['scramble', 'fourball', 'shamble'].includes(g))
  const holes = Array.from({ length: 18 }, (_, i) => i + 1)
  const netOf = (p, h, gross) => gross != null ? gross - (round.useHandicap ? (p.strokesPerHole?.[h - 1] || 0) : 0) : null
  return (
    <div>
      <div className="text-sm mb-2">{editable ? <span className="font-bold" style={{ color: PRIMARY }}>Scoring Group {m.matchNumber} — enter gross{round.useHandicap ? ', net auto' : ''}</span> : <span className="text-gray-500">Group {m.matchNumber} — read-only</span>}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#f6f6f2', color: '#555' }}>
            <th style={{ border: '1px solid #eee', padding: '4px 6px' }}>H</th><th style={{ border: '1px solid #eee', padding: '4px 6px' }}>Par</th>
            {gp.map((p) => <th key={p.id} style={{ border: '1px solid #eee', padding: '4px 6px', minWidth: 74, background: teamGame ? (p.team === 'B' ? '#FBF3DC' : '#EDF4EE') : undefined }}>{p.name}<div className="text-[10px] font-normal text-gray-400">{teamGame ? `${p.team} · ` : ''}PH {p.playingHandicap}</div></th>)}
          </tr></thead>
          <tbody>{holes.map((h) => (
            <tr key={h}>
              <td style={{ border: '1px solid #eee', padding: '4px 6px', fontWeight: 700 }}>{h}</td>
              <td style={{ border: '1px solid #eee', padding: '4px 6px', color: '#888' }}>{pars[h - 1] ?? ''}</td>
              {gp.map((p) => { const gross = num(valOf(mid, h, p.id)); const net = netOf(p, h, gross); const strk = round.useHandicap ? (p.strokesPerHole?.[h - 1] || 0) : 0; return (
                <td key={p.id} style={{ border: '1px solid #eee', padding: '2px 4px', textAlign: 'center' }}>
                  {editable ? <input className="border rounded w-11 text-center py-1" inputMode="numeric" value={valOf(mid, h, p.id)} onChange={(e) => write(mid, h, p.id, e.target.value.replace(/[^0-9]/g, ''))} /> : <span style={{ fontWeight: 700 }}>{gross ?? '·'}</span>}
                  {round.useHandicap ? <div className="text-[10px] text-gray-400">{gross != null ? `${strk ? '●'.repeat(Math.min(strk, 2)) + ' ' : ''}${net}` : (strk ? '●'.repeat(Math.min(strk, 2)) : '')}</div> : null}
                </td>
              )})}
            </tr>
          ))}</tbody>
        </table>
      </div>
      {editable && !m.complete ? <button className="btn-primary mt-3" style={{ backgroundColor: PRIMARY }} onClick={() => { if (confirm('Finish this group?')) finalizeMatch(id, mid) }}>Finish Group {m.matchNumber}</button> : null}
      {m.complete ? <p className="text-sm font-bold mt-3" style={{ color: PRIMARY }}>Group {m.matchNumber} finished ✓</p> : null}
    </div>
  )
}

// Game-aware live follow view — renders the app's published round.follow snapshot
// verbatim: title, running money standings, side bets (Scats/Skins/Dots), and
// per-hole game detail (Wolf: who was wolf, partner/lone, who won). Read-only.
function FollowView({ f, round }) {
  const toneColor = (t) => (t === 'win' ? '#1B7A3E' : t === 'lose' ? '#B42318' : '#8A6D1F')
  const fmtPts = (n) => (Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10))
  const rows = (f.holes || []).filter((h) => h.played || h.hole === f.currentHole).sort((a, b) => a.hole - b.hole)
  const [selHole, setSelHole] = useState(null)
  const cur = selHole != null ? selHole : (rows.length ? rows[rows.length - 1].hole : null)
  const selIdx = rows.findIndex((h) => h.hole === cur)
  const selRow = selIdx >= 0 ? rows[selIdx] : null
  const isLatest = selIdx < 0 || selIdx === rows.length - 1
  const step = (d) => { const ni = selIdx + d; if (ni >= 0 && ni < rows.length) setSelHole(rows[ni].hole) }

  // REWIND: reconstruct the WHOLE page as of the selected hole. Two generic
  // dimensions any game can drive:
  //   • standings-through-N — per-hole standDelta (Team Cup team points) if
  //     present, else the sum of each player's per-hole money (players[].net).
  //   • side-bets-through-N — per-hole sideDelta counts (Scats/Skins/Dots/CTH)
  //     if present, else the legacy per-hole scat flag.
  const allHoles = f.holes || []
  const upto = allHoles.filter((h) => h.hole <= cur && (Array.isArray(h.players) || h.standDelta))
  const canRewind = rows.some((h) => (Array.isArray(h.players) && h.players.length) || h.standDelta)
  const standValue = (rowId) => upto.reduce((s, h) => {
    if (h.standDelta && rowId in h.standDelta) return s + (Number(h.standDelta[rowId]) || 0)
    const pp = Array.isArray(h.players) ? h.players.find((x) => x.id === rowId) : null
    return s + (pp ? Number(pp.net) || 0 : 0)
  }, 0)
  const sideKeys = new Set(); allHoles.forEach((h) => h.sideDelta && Object.keys(h.sideDelta).forEach((k) => sideKeys.add(k)))
  const sideThrough = (key, pid) => upto.reduce((c, h) => c + (Number(h.sideDelta?.[key]?.[pid]) || 0), 0)
  const scatLegacy = (pid) => upto.reduce((c, h) => c + (Array.isArray(h.players) && h.players.find((x) => x.id === pid)?.scat ? 1 : 0), 0)
  const canRewSide = (key) => sideKeys.has(key) || (key === 'scats' && rows.some((h) => Array.isArray(h.players) && h.players.some((p) => p.scat)))
  const sideCount = (key, pid) => (sideKeys.has(key) ? sideThrough(key, pid) : scatLegacy(pid))
  const fmtSide = (key, n) => (key === 'skins' ? `${n} skin${n === 1 ? '' : 's'}` : String(n))

  const rewind = canRewind && !isLatest
  const renderStand = (r) => (r.unit === 'pts' ? `${fmtPts(r.net)} ${r.unit}` : money(r.net))
  const standColor = (r) => (r.unit === 'pts' ? PRIMARY : r.net > 0 ? '#1B7A3E' : r.net < 0 ? '#B42318' : '#888')
  const standings = rewind ? (f.standings || []).map((r) => ({ ...r, net: standValue(r.id), text: undefined })) : (f.standings || [])
  const standingsLabel = (f.standingsLabel || 'Standings') + (rewind ? ` · THROUGH HOLE ${cur}` : '')
  const teamColLabel = f.teamColLabel || 'Team'

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1"><span style={{ fontSize: 30 }}>⛳</span><h3 className="font-bold" style={{ fontSize: 30 }}>{f.title}{f.net ? ' · Net' : ''}</h3></div>
      <p className="text-gray-600 mb-3" style={{ fontSize: 16 }}>{round?.course?.name ? round.course.name + ' · ' : ''}Hole {f.currentHole} · following live (read-only)</p>
      {rewind ? <div className="mb-4 rounded-lg" style={{ background: '#FFF6DD', border: '1px solid #E8D9AE', color: '#8A6D1F', padding: '9px 13px', fontSize: 15, fontWeight: 700 }}>⏪ Rewound to Hole {cur} — the standings, side bets &amp; detail below are as they stood then. Tap Hole {rows[rows.length - 1].hole} for live.</div> : null}

      <div className="font-extrabold uppercase tracking-wide mb-2" style={{ color: PRIMARY, fontSize: 17 }}>{standingsLabel}</div>
      <table className="w-full mb-6" style={{ borderCollapse: 'collapse', fontSize: 21 }}><tbody>
        {standings.map((r, i) => (
          <tr key={r.id || i}>
            <td style={{ border: '1px solid #e5e5e5', padding: '11px 14px', fontWeight: 700 }}>{r.name}</td>
            <td style={{ border: '1px solid #e5e5e5', padding: '11px 14px', textAlign: 'right', fontWeight: 900, fontSize: 22, color: standColor(r) }}>{r.text ? r.text : renderStand(r)}</td>
          </tr>
        ))}
      </tbody></table>

      {(f.sideBets || []).map((sb) => {
        const showRew = rewind && canRewSide(sb.key)
        return (
          <div key={sb.key} className="mb-6">
            <div className="font-extrabold" style={{ color: PRIMARY, fontSize: 19 }}>{sb.label}</div>
            <div className="text-gray-500 mb-2" style={{ fontSize: 15 }}>{sb.note}{showRew ? ` · as of Hole ${cur}` : ''}</div>
            <div className="flex flex-wrap" style={{ gap: 7 }}>
              {(sb.rows || []).map((r, i) => {
                const val = showRew && !r.out ? fmtSide(sb.key, sideCount(sb.key, r.id)) : r.value
                return (
                  <span key={r.id || i} className="font-bold rounded-lg inline-flex items-baseline" style={{ fontSize: 15, padding: '5px 10px', gap: 4, ...(r.out ? { background: '#F1F1EE', color: '#999', textDecoration: 'line-through' } : { background: '#EDF4EE', color: PRIMARY }) }}>
                    <span>{r.name}</span><span style={{ fontSize: 18, fontWeight: 900 }}>{val}</span>
                  </span>
                )
              })}
            </div>
          </div>
        )
      })}

      <div className="flex items-center gap-2 mb-2">
        <div className="font-extrabold uppercase tracking-wide" style={{ color: PRIMARY, fontSize: 17 }}>Hole by hole</div>
        <span className="font-bold rounded-full" style={{ fontSize: 14, padding: '3px 11px', background: '#1B7A3E', color: '#fff' }}>● LIVE · Hole {f.currentHole}</span>
      </div>
      {/* Tap any hole (or ‹ ›) to rewind the WHOLE page to that hole — standings,
          scats, and the full player-by-player detail all reflect that point. */}
      {rows.length ? (
        <>
          <div className="flex items-center flex-wrap" style={{ gap: 6, marginBottom: 12 }}>
            <button onClick={() => step(-1)} disabled={selIdx <= 0} className="font-extrabold" style={{ fontSize: 20, width: 38, height: 38, borderRadius: 10, border: '1px solid #ddd', background: '#fff', color: PRIMARY, opacity: selIdx <= 0 ? 0.4 : 1 }}>‹</button>
            {rows.map((h) => {
              const isSel = h.hole === cur, live = h.hole === f.currentHole
              return (
                <button key={h.hole} onClick={() => setSelHole(h.hole)} className="font-extrabold" style={{ minWidth: 42, height: 38, borderRadius: 10, fontSize: 17, border: live ? '2px solid #1B7A3E' : '1px solid transparent', ...(isSel ? { background: PRIMARY, color: '#fff' } : { background: '#ECECEA', color: PRIMARY }) }}>{h.hole}{live ? ' ●' : ''}</button>
              )
            })}
            <button onClick={() => step(1)} disabled={selIdx >= rows.length - 1} className="font-extrabold" style={{ fontSize: 20, width: 38, height: 38, borderRadius: 10, border: '1px solid #ddd', background: '#fff', color: PRIMARY, opacity: selIdx >= rows.length - 1 ? 0.4 : 1 }}>›</button>
          </div>
          {selRow ? <HoleDetail h={selRow} live={selRow.hole === f.currentHole} toneColor={toneColor} teamColLabel={teamColLabel} /> : null}
        </>
      ) : <p className="text-gray-500" style={{ fontSize: 17 }}>No holes scored yet.</p>}
    </div>
  )
}

// Full scorer-view detail for ONE hole, game-agnostic. Each player shows their
// TEAM/ROLE that hole (published as teamText+tag, e.g. Wolf/partner/field, Pair
// A/B, Red/Blue), their SCORE, and their result for the hole — money (green/red)
// or a game-specific holeText (Team Cup team points, Dogfight quota).
function HoleDetail({ h, live, toneColor, teamColLabel }) {
  const hasPlayers = Array.isArray(h.players) && h.players.length
  const anyScat = hasPlayers && h.players.some((p) => p.scat)
  const anyRole = hasPlayers && h.players.some((p) => p.role)
  const anyPartner = hasPlayers && h.players.some((p) => p.role === 'partner')
  const roleTag = (p) => (p.role === 'wolf' ? '🐺 ' : p.role === 'partner' ? '🤝 ' : '')
  const roleLabel = (p) => (p.role === 'wolf' ? 'Wolf' : p.role === 'partner' ? 'w/ Wolf' : p.role === 'field' ? 'Field' : '')
  const tagOf = (p) => (p.tag ? p.tag + ' ' : roleTag(p))
  const teamTxt = (p) => (p.teamText != null ? p.teamText : roleLabel(p))
  return (
    <div style={{ border: '1px solid #e5e5e5', borderRadius: 12, padding: 14, background: live ? '#F4FBF6' : '#fff' }}>
      <div className="flex items-center justify-between mb-1" style={{ gap: 8 }}>
        <div className="font-extrabold" style={{ fontSize: 22, color: PRIMARY }}>Hole {h.hole}{live ? <span className="font-bold" style={{ marginLeft: 8, fontSize: 12.5, padding: '2px 9px', borderRadius: 999, background: '#1B7A3E', color: '#fff' }}>● LIVE</span> : null}</div>
        {h.pick ? <div style={{ fontSize: 15, color: '#555' }}>{h.pick}</div> : null}
      </div>
      {h.teamLabel ? <div className="mb-3" style={{ fontSize: 17, fontWeight: 700 }}>{h.teamLabel}</div> : null}
      {hasPlayers ? (
        <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: 18 }}>
          <thead><tr style={{ color: '#999', fontSize: 12.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            <th style={{ textAlign: 'left', padding: '4px 8px' }}>Player</th>
            <th style={{ textAlign: 'left', padding: '4px 8px' }}>{teamColLabel}</th>
            <th style={{ textAlign: 'center', padding: '4px 8px' }}>Score</th>
            <th style={{ textAlign: 'right', padding: '4px 8px' }}>This hole</th>
          </tr></thead>
          <tbody>
            {h.players.map((p) => (
              <tr key={p.id}>
                <td style={{ border: '1px solid #eee', padding: '9px 10px', fontWeight: 700 }}>{tagOf(p)}{p.name}{p.scat ? ' 💰' : ''}</td>
                <td style={{ border: '1px solid #eee', padding: '9px 10px', fontWeight: 600, color: p.teamColor || (p.role === 'field' ? '#777' : PRIMARY) }}>{teamTxt(p)}</td>
                <td style={{ border: '1px solid #eee', padding: '9px 10px', textAlign: 'center', fontWeight: 800 }}>{p.score != null ? p.score : '·'}</td>
                <td style={{ border: '1px solid #eee', padding: '9px 10px', textAlign: 'right', fontWeight: 900, color: p.holeText != null ? '#333' : p.net > 0 ? '#1B7A3E' : p.net < 0 ? '#B42318' : '#888' }}>{p.holeText != null ? p.holeText : money(p.net)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {hasPlayers && (anyRole || anyScat) ? <div className="mt-1" style={{ fontSize: 13, color: '#999' }}>{anyRole ? '🐺 wolf' : ''}{anyPartner ? ' · 🤝 partner' : ''}{anyScat ? `${anyRole ? ' · ' : ''}💰 scat` : ''}</div> : null}
      <div className="mt-2" style={{ fontWeight: 800, fontSize: 16, color: h.outcome ? toneColor(h.tone) : '#888' }}>{h.outcome || (live ? 'In progress…' : '')}</div>
    </div>
  )
}

function ResultsView({ results }) {
  if (!results) return null
  const gEntries = Object.entries(results.games || {})
  return (
    <div>
      {gEntries.length === 0 ? <p className="text-gray-500 text-sm">Enter scores to see results.</p> : null}
      {gEntries.map(([gid, g]) => (
        <div key={gid} className="mb-4">
          <div className="text-sm font-extrabold uppercase tracking-wide mb-1" style={{ color: PRIMARY }}>{g.label}{g.net ? ' · net' : ' · gross'}{g.status ? ` · ${g.status}` : ''}</div>
          <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
            <tbody>{g.standings.map((r, i) => (
              <tr key={i}>
                <td style={{ border: '1px solid #eee', padding: '4px 8px', fontWeight: 700 }}>{r.name}</td>
                <td style={{ border: '1px solid #eee', padding: '4px 8px', color: '#666' }}>{r.detail}</td>
                <td style={{ border: '1px solid #eee', padding: '4px 8px', textAlign: 'right', fontWeight: 800, color: r.net > 0 ? '#1B7A3E' : r.net < 0 ? '#B42318' : '#888' }}>{money(r.net)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ))}
      {results.settleUp?.length ? (
        <div className="mt-4">
          <div className="text-sm font-extrabold uppercase tracking-wide mb-1" style={{ color: PRIMARY }}>Settle up (all games)</div>
          {results.settleUp.map((t, i) => <div key={i} className="flex justify-between py-1 border-b text-sm" style={{ borderColor: '#eee' }}><span><b>{t.from}</b> pays <b>{t.to}</b></span><span className="font-extrabold" style={{ color: PRIMARY }}>${t.amt}</span></div>)}
        </div>
      ) : gEntries.length ? <p className="text-sm text-gray-500 mt-2">All square so far.</p> : null}
    </div>
  )
}
